import axios from 'axios';
import { getAccessToken, setAccessToken, clearAccessToken } from './tokenStore';

/**
 * Primary API client.
 * - withCredentials: true  →  sends the HttpOnly refreshToken cookie on every request
 * - Request interceptor   →  attaches the in-memory access token as Bearer header
 * - Response interceptor  →  on HTTP 401: silently refreshes, queues concurrent
 *                            requests, then retries; on refresh failure redirects to login
 */
const api = axios.create({
  baseURL: process.env.REACT_APP_API_LINK,
  withCredentials: true,
});

/**
 * Dedicated instance used only for token-refresh calls.
 * Has no response interceptor so it cannot trigger a recursive refresh loop.
 */
const refreshApi = axios.create({
  baseURL: process.env.REACT_APP_API_LINK,
  withCredentials: true,
});

export const refreshAccessToken = () => refreshApi.post('/auth/refresh');

// ── Queue management for concurrent 401s ────────────────────────────────────

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(token);
  });
  failedQueue = [];
};

// ── Request interceptor: attach access token ────────────────────────────────

api.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ── Response interceptor: silent refresh on 401 ─────────────────────────────

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Do not retry if already retried, or if it's not a 401
    if (originalRequest._retry || error.response?.status !== 401) {
      return Promise.reject(error);
    }

    // Another refresh is already in-flight — queue this request
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      })
        .then((token) => {
          originalRequest._retry = true;
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        })
        .catch((err) => Promise.reject(err));
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      const res = await refreshAccessToken();
      const newToken = res.data.token;
      setAccessToken(newToken);
      processQueue(null, newToken);
      originalRequest.headers.Authorization = `Bearer ${newToken}`;
      return api(originalRequest);
    } catch (refreshError) {
      clearAccessToken();
      processQueue(refreshError, null);
      // Refresh token is invalid/expired — send user to login
      window.location.href = '/';
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);

export default api;
